import React from 'react';
import { useTranslation } from 'react-i18next';
import { EnvironmentOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { openState, todayIndex, formatPrice, directionsUrl, mapEmbedUrl, cleanDays, cleanMenuItems } from '../utils/blocks';

// Monday-first weekday names in the reader's language (1 Jan 2024 was a Monday)
const dayName = (index, lang, style = 'long') =>
  new Date(Date.UTC(2024, 0, 1 + index)).toLocaleDateString(lang, { weekday: style, timeZone: 'UTC' });

const HoursBlock = ({ link, lang }) => {
  const [t] = useTranslation();
  const hours = { ...link.data, days: cleanDays(link.data.days) };
  const state = openState(hours);
  const today = todayIndex(hours);
  let status = '';
  if (state && state.open) status = t('blocks.openUntil', { time: state.closesAt });
  else if (state && state.opensAt) {
    status = state.opensDay === today
      ? t('blocks.opensAt', { time: state.opensAt })
      : t('blocks.opensOn', { day: dayName(state.opensDay, lang), time: state.opensAt });
  }

  return (
    <details className="profile-block profile-hours">
      <summary>
        <ClockCircleOutlined className="profile-block__icon" />
        <span className={`profile-hours__badge ${state && state.open ? 'is-open' : ''}`}>{state && state.open ? t('blocks.openNow') : t('blocks.closedNow')}</span>
        <span className="profile-hours__status">{status}</span>
      </summary>
      <ul className="profile-hours__week">
        {hours.days.map((day, i) => (
          <li key={i} className={i === today ? 'is-today' : ''}>
            <span>{dayName(i, lang)}</span>
            <span>{day.closed ? t('blocks.closed') : `${day.open} – ${day.close}`}</span>
          </li>
        ))}
      </ul>
      {hours.note && <p className="profile-block__note">{String(hours.note)}</p>}
    </details>
  );
};

const LocationBlock = ({ link, anchorProps }) => {
  const [t] = useTranslation();
  const { address, showMap } = link.data;
  return (
    <div className="profile-block profile-location">
      {/* Out of the tab order: the map is a picture of the address, and the
          directions button below is the way into it */}
      {showMap &&
        <iframe className="profile-location__map" src={mapEmbedUrl(address)} title={address} tabIndex="-1" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />}
      <div className="profile-location__body">
        <EnvironmentOutlined className="profile-block__icon" />
        <address>{address}</address>
      </div>
      <a className="profile-block__action" {...anchorProps(directionsUrl(address))}>{t('blocks.directions')}</a>
    </div>
  );
};

const MenuBlock = ({ link, items, lang }) => (
  <div className="profile-block profile-menu">
    {link.linkTitle && <h3 className="profile-block__title">{link.linkTitle}</h3>}
    <ul>
      {items.map((item, i) => (
        <li key={i}>
          <div className="profile-menu__line">
            <span className="profile-menu__name">{item.name}</span>
            {item.price !== null && item.price !== undefined && <>
              <span className="profile-menu__dots" aria-hidden="true" />
              <span className="profile-menu__price">{formatPrice(item.price, lang)}</span>
            </>}
          </div>
          {item.description && <p className="profile-menu__desc">{item.description}</p>}
        </li>
      ))}
    </ul>
  </div>
);

/*
  A content block on the profile page. `anchorProps(url)` comes from
  ProfileView so the directions button behaves like any other link there
  (tracked on the public page, inert in the dashboard preview).
*/
const ProfileBlock = ({ link, anchorProps }) => {
  const [, i18n] = useTranslation();
  // resolvedLanguage, not language: a German browser reads the English
  // strings (the fallback) and should get English day names with them
  const lang = (i18n.resolvedLanguage || i18n.language || '').slice(0, 2) === 'tr' ? 'tr' : 'en';
  const data = link.data || {};
  if (link.kind === 'hours' && Array.isArray(data.days) && data.days.length === 7) return <HoursBlock link={link} lang={lang} />;
  if (link.kind === 'location' && typeof data.address === 'string' && data.address.trim()) return <LocationBlock link={link} anchorProps={anchorProps} />;
  if (link.kind === 'menu' && Array.isArray(data.items)) {
    const items = cleanMenuItems(data.items);
    return items.length ? <MenuBlock link={link} items={items} lang={lang} /> : null;
  }
  return null;
};

export default ProfileBlock;
